import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import icons from './Icons';
import PlaceholderImage from './PlaceholderImage';
import './home.css';

const Home = ({ onNavigate }) => {
  const [scrollPct, setScrollPct] = useState(0);
  const [hoveredClient, setHoveredClient] = useState(null);

  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const pct = (el.scrollTop / (el.scrollHeight - el.clientHeight)) * 100;
      setScrollPct(Math.min(pct, 100));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNavClick = (e, pageId) => {
    e.preventDefault();
    if (onNavigate) {
      onNavigate(pageId);
    }
  };

  return (
    <>
      {/* ── Scroll progress bar ── */}
      <div
        className="scroll-progress-bar"
        style={{ width: `${scrollPct}%` }}
        role="progressbar"
        aria-valuenow={Math.round(scrollPct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Page scroll progress"
      />

      {/* ══════════════════════════════════
          HERO SECTION
      ══════════════════════════════════ */}
      <section className="hero-section" aria-label="Hero">
        {/* Dynamic Tech Mesh Background */}
        <div className="hero-mesh-bg" aria-hidden="true" />

        {/* Ambient glows */}
        <div className="hero-glow-orange" aria-hidden="true" />
        <div className="hero-glow-blue" aria-hidden="true" />

        {/* Content */}
        <Container className="hero-content">
          <Row className="justify-content-center">
            <Col xs={12} lg={10} xl={9} className="text-center">
              {/* Trust & Location Badge */}
              <div className="d-inline-flex align-items-center gap-2 mb-4 px-3 py-2 rounded-pill badge-premium-glowing">
                <span className="text-orange">★</span>
                <span>Performance Marketing &bull; CRM Automation &bull; Custom Software</span>
              </div>

              {/* Clear, High-Impact Headline */}
              <h1 className="hero-title">
                Building Connected Digital Growth
                <span className="hero-title-gradient d-block mt-1">for Growing Businesses.</span>
              </h1>

              {/* Natural, Professional Subheadline */}
              <p className="hero-subtitle">
                MSquare Professionals connects data-driven customer acquisition, automated CRM workflows, and custom software systems into one reliable growth engine for your business.
              </p>

              {/* CTA Buttons */}
              <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center align-items-center">
                <a
                  href="#contact"
                  onClick={(e) => handleNavClick(e, 'contact')}
                  className="btn-hero-primary w-100 w-sm-auto"
                >
                  <span>Get Free Consultation</span>
                  {icons.arrowRight}
                </a>
                <a
                  href="#services"
                  onClick={(e) => handleNavClick(e, 'services')}
                  className="btn-hero-secondary w-100 w-sm-auto"
                >
                  Explore Services
                </a>
              </div>
            </Col>
          </Row>
        </Container>

        {/* Bottom fade into next section */}
        <div className="hero-bottom-fade" aria-hidden="true" />
      </section>

      {/* ══════════════════════════════════
          TRUST METRICS SECTION
      ══════════════════════════════════ */}
      <section className="stats-section" aria-label="Key statistics">
        <Container>
          <div className="text-center mb-5">
            <span className="badge-eyebrow mb-2">VERIFIED IMPACT</span>
            <h2 className="section-title">Trusted by Growing Businesses</h2>
            <p className="section-subtitle">
              From high-growth startups to enterprise groups — we engineer the digital infrastructure that makes scale predictable.
            </p>
          </div>

          <Row className="g-4 justify-content-center">
            {[
              { number: '100+', label: 'Platforms Built', colorClass: 'orange' },
              { number: '3', label: 'Core Practice Areas', colorClass: 'blue' },
              { number: '98%', label: 'Client Retention', colorClass: 'orange' },
              { number: '3.2x', label: 'Average ROI Delivered', colorClass: 'blue' }
            ].map(({ number, label, colorClass }) => (
              <Col key={label} xs={6} lg={3}>
                <div className="stat-card">
                  <div className={`stat-number ${colorClass}`}>{number}</div>
                  <div className="stat-label">{label}</div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>


      {/* ══════════════════════════════════
          ECOSYSTEM SERVICES SECTION
      ══════════════════════════════════ */}
      <section className="services-section" aria-label="Our services">
        <Container>
          <div className="text-center mb-5">
            <span className="badge-eyebrow mb-2">INTEGRATED ENGINE</span>
            <h2 className="section-title">One Partner. One Ecosystem.</h2>
            <p className="section-subtitle">
              Marketing, automation, and technology — designed to work together as a single, connected growth system.
            </p>
          </div>

          <Row className="g-4">
            {/* ── Card 1: Growth Marketing ── */}
            <Col xs={12} lg={4} className="d-flex">
              <div className="service-card orange-hover w-100 d-flex flex-column">
                <div className="p-4 flex-grow-1">
                  <div className="service-icon-box orange">
                    {icons.chart}
                  </div>
                  <h3 className="service-card-title">Growth Marketing</h3>
                  <p className="service-card-desc">
                    Attract the right customers and generate qualified leads through data-driven performance marketing.
                  </p>

                  <div className="my-3">
                    <PlaceholderImage
                      width={380}
                      height={200}
                      title="Performance Engine"
                      category="Marketing"
                      theme="orange"
                    />
                  </div>

                  <ul className="service-card-list">
                    {[
                      'Performance Marketing & Lead Generation',
                      'SEO, SEM & Search Dominance',
                      'Social Media Marketing',
                      'Branding & Digital Strategy'
                    ].map((item) => (
                      <li key={item}>
                        <span className="check-icon-orange">{icons.checkCircle}</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="px-4 pb-4">
                  <a
                    href="#services"
                    onClick={(e) => handleNavClick(e, 'services')}
                    className="service-card-link"
                  >
                    <span>View Marketing Pillar</span>
                    {icons.arrowRight}
                  </a>
                </div>
              </div>
            </Col>

            {/* ── Card 2: Business Automation ── */}
            <Col xs={12} lg={4} className="d-flex">
              <div className="service-card blue-hover w-100 d-flex flex-column">
                <div className="p-4 flex-grow-1">
                  <div className="service-icon-box blue">
                    {icons.automation}
                  </div>
                  <h3 className="service-card-title">Business Automation</h3>
                  <p className="service-card-desc">
                    Eliminate manual work and build connected systems that scale your operations seamlessly.
                  </p>

                  <div className="my-3">
                    <PlaceholderImage
                      width={380}
                      height={200}
                      title="Connected Workflows"
                      category="Automation"
                      theme="blue"
                    />
                  </div>

                  <ul className="service-card-list">
                    {[
                      'CRM Solutions & Lead Management',
                      'Workflow & Sales Automation',
                      'Attendance Management Systems',
                      'Process Digitization'
                    ].map((item) => (
                      <li key={item}>
                        <span className="check-icon-blue">{icons.checkCircle}</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="px-4 pb-4">
                  <a
                    href="#services"
                    onClick={(e) => handleNavClick(e, 'services')}
                    className="service-card-link"
                  >
                    <span>View Automation Pillar</span>
                    {icons.arrowRight}
                  </a>
                </div>
              </div>
            </Col>

            {/* ── Card 3: Technology Solutions ── */}
            <Col xs={12} lg={4} className="d-flex">
              <div className="service-card purple-hover w-100 d-flex flex-column">
                <div className="p-4 flex-grow-1">
                  <div className="service-icon-box neutral">
                    {icons.code}
                  </div>
                  <h3 className="service-card-title">Technology Solutions</h3>
                  <p className="service-card-desc">
                    Custom software, web and mobile apps, and enterprise platforms built specifically for your business.
                  </p>

                  <div className="my-3">
                    <PlaceholderImage
                      width={380}
                      height={200}
                      title="Custom Software"
                      category="Technology"
                      theme="purple"
                    />
                  </div>

                  <ul className="service-card-list">
                    {[
                      'Custom Software & Web Applications',
                      'Mobile Apps (iOS & Android)',
                      'Enterprise Software & ERP Solutions',
                      'API Integrations & Pipelines'
                    ].map((item) => (
                      <li key={item}>
                        <span className="check-icon-muted">{icons.checkCircle}</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="px-4 pb-4">
                  <a
                    href="#services"
                    onClick={(e) => handleNavClick(e, 'services')}
                    className="service-card-link"
                  >
                    <span>View Tech Pillar</span>
                    {icons.arrowRight}
                  </a>
                </div>
              </div>
            </Col>
          </Row>

          {/* View All Services Button */}
          <div className="text-center mt-5">
            <a
              href="#services"
              onClick={(e) => handleNavClick(e, 'services')}
              className="btn-view-services"
            >
              <span>View All 15+ Practice Areas</span>
              {icons.arrowRight}
            </a>
          </div>
        </Container>
      </section>


      {/* ══════════════════════════════════
          BUZZWORTHY-STYLE INTERACTIVE CLIENT IMPACT STRIP
      ══════════════════════════════════ */}
      <section className="buzz-clients-impact-section py-5" aria-label="Client results">
        <Container>
          <div className="text-center mb-4">
            <span className="badge-eyebrow mb-2">VERIFIED METRICS</span>
            <h2 className="section-title">Measurable Ecosystem Returns</h2>
            <p className="section-subtitle">
              Hover over each client partner to see verified conversion and pipeline KPIs delivered.
            </p>
          </div>

          <div className="buzz-client-grid">
            {[
              {
                id: 1,
                name: 'D2C Fashion & Apparel',
                category: 'E-Commerce',
                stat: '4.1x',
                metric: 'ROAS Delivered',
                sub: 'Targeted Meta ads, high-converting product funnels & analytics'
              },
              {
                id: 2,
                name: 'Residential Property Group',
                category: 'Real Estate',
                stat: '800+',
                metric: 'Qualified Leads in 60 Days',
                sub: 'Hyper-local ad campaigns & instant WhatsApp CRM routing'
              },
              {
                id: 3,
                name: 'Regional Clinic Network',
                category: 'Healthcare',
                stat: '-42%',
                metric: 'Cost Per Acquisition',
                sub: 'High-intent search campaigns & patient booking workflows'
              },
              {
                id: 4,
                name: 'B2B Enterprise Services',
                category: 'B2B & SaaS',
                stat: '2.8x',
                metric: 'Pipeline Growth',
                sub: 'Automated CRM lead scoring & custom client portals'
              }
            ].map((client) => {
              const isHovered = hoveredClient === client.id;
              return (
                <div
                  key={client.id}
                  className={`buzz-client-box ${isHovered ? 'is-active' : ''}`}
                  onMouseEnter={() => setHoveredClient(client.id)}
                  onMouseLeave={() => setHoveredClient(null)}
                >
                  <div className="buzz-client-badge">{client.category}</div>
                  <h4 className="buzz-client-name">{client.name}</h4>
                  
                  <div className="buzz-client-impact">
                    <span className="buzz-impact-number">{client.stat}</span>
                    <span className="buzz-impact-label">{client.metric}</span>
                  </div>
                  
                  <p className="buzz-client-sub">{client.sub}</p>
                </div>
              );
            })}
          </div>
        </Container>
      </section>


      {/* ══════════════════════════════════
          FEATURED CASE STUDIES STRIP
      ══════════════════════════════════ */}
      <section className="featured-cases-section py-5 bg-secondary-alt">
        <Container>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-end mb-5">
            <div>
              <span className="badge-eyebrow mb-2">PROVEN OUTCOMES</span>
              <h2 className="section-title m-0">Recent Client Results</h2>
            </div>
            {/* Styled Explore All Case Studies Button */}
            <a
              href="#case-studies"
              onClick={(e) => handleNavClick(e, 'case-studies')}
              className="btn-explore-cases mt-3 mt-md-0"
            >
              <span>Explore All Case Studies</span>
              {icons.arrowRight}
            </a>
          </div>

          <Row className="g-4">
            <Col xs={12} md={6}>
              <Card className="home-case-card p-0 overflow-hidden border-0 rounded-4">
                <PlaceholderImage
                  width={600}
                  height={320}
                  title="D2C Fashion & Apparel"
                  category="4.1x ROAS • E-Commerce"
                  theme="orange"
                />
                <Card.Body className="p-4">
                  <span className="case-tag-small">E-COMMERCE & FASHION</span>
                  <h3 className="fs-5 fw-bold mt-2">Scaling D2C E-Commerce: 4.1x ROAS in 90 Days</h3>
                  <p className="text-secondary small mb-3">
                    Rebuilt advertising campaigns, creative testing matrix, and fast landing pages with full conversion attribution.
                  </p>
                  <a
                    href="#case-studies"
                    onClick={(e) => handleNavClick(e, 'case-studies')}
                    className="service-card-link"
                  >
                    <span>Read Full Case Study</span>
                    {icons.arrowRight}
                  </a>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} md={6}>
              <Card className="home-case-card p-0 overflow-hidden border-0 rounded-4">
                <PlaceholderImage
                  width={600}
                  height={320}
                  title="Residential Property Group"
                  category="800+ Leads • Real Estate"
                  theme="blue"
                />
                <Card.Body className="p-4">
                  <span className="case-tag-small">REAL ESTATE & PROPTECH</span>
                  <h3 className="fs-5 fw-bold mt-2">Real Estate Lead Gen: 800+ Verified Inquiries in 60 Days</h3>
                  <p className="text-secondary small mb-3">
                    Hyper-local ad campaigns with multi-step qualification funnels and real-time WhatsApp CRM lead distribution.
                  </p>
                  <a
                    href="#case-studies"
                    onClick={(e) => handleNavClick(e, 'case-studies')}
                    className="service-card-link"
                  >
                    <span>Read Full Case Study</span>
                    {icons.arrowRight}
                  </a>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>


      {/* ══════════════════════════════════
          OUR WORKING PRINCIPLES
      ══════════════════════════════════ */}
      <section className="buzz-principles-section py-5" aria-label="Our working principles">
        <Container>
          <div className="text-center mb-5">
            <span className="badge-eyebrow mb-2">OUR STANDARDS</span>
            <h2 className="section-title">How We Deliver Work</h2>
            <p className="section-subtitle">
              Clear accountability, transparent communication, and solid engineering behind every deliverable.
            </p>
          </div>

          <div className="buzz-principles-stack">
            {[
              {
                rule: '01',
                title: 'CLEAR ROI & ATTRIBUTION',
                body: 'We eliminate vanity metrics. Every campaign rupee and software automation is linked directly to qualified leads and verified sales pipeline.',
                tag: 'Accountability',
                accent: 'orange'
              },
              {
                rule: '02',
                title: 'ONE CONNECTED ENGINE',
                body: 'Working with separate, disconnected agencies leads to miscommunication and lost leads. We connect marketing, CRM databases, and custom software under one roof.',
                tag: 'Integration',
                accent: 'blue'
              },
              {
                rule: '03',
                title: 'RELIABLE ENGINEERING',
                body: 'We build digital systems with software engineering rigor. Sub-second page loads, automated error alerts, and clean database integrations that scale seamlessly.',
                tag: 'Quality',
                accent: 'purple'
              },
              {
                rule: '04',
                title: 'DIRECT PRACTITIONER ACCESS',
                body: 'You communicate directly with senior specialists who manage your campaigns and code your software. Transparent sprint updates, clear timelines, and prompt support.',
                tag: 'Direct Access',
                accent: 'orange'
              }
            ].map((principle, index) => (
              <div
                key={principle.rule}
                className={`buzz-principle-card accent-${principle.accent}`}
                style={{ '--stack-index': index }}
              >
                <div className="buzz-principle-top">
                  <span className="buzz-rule-number">{principle.rule}</span>
                  <span className="buzz-rule-tag">{principle.tag}</span>
                </div>
                <h3 className="buzz-principle-title">{principle.title}</h3>
                <p className="buzz-principle-body">{principle.body}</p>
                <div className="buzz-principle-bar"></div>
              </div>
            ))}
          </div>
        </Container>
      </section>


      {/* ══════════════════════════════════
          CTA BANNER SECTION
      ══════════════════════════════════ */}
      <section className="cta-section" aria-label="Call to action">
        <div className="cta-ellipse-glow" aria-hidden="true" />
        <Container className="cta-content">
          <Row className="justify-content-center">
            <Col xs={12} lg={8} className="text-center">
              <h2 className="cta-title">Ready to Make Growth Predictable?</h2>
              <p className="cta-desc">
                Stop juggling multiple disconnected vendors. Partner with M Square to build an integrated growth infrastructure that generates revenue and scales your operations.
              </p>
              <a
                href="#contact"
                onClick={(e) => handleNavClick(e, 'contact')}
                className="btn-cta-primary"
                aria-label="Build your growth system with MSquare"
              >
                <span>Build Your Growth System</span>
                {icons.arrowRight}
              </a>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default Home;